import React, { useState, useRef, useEffect } from 'react';
import { 
  Card, Table, Button, Space, Input, Form, Select, 
  Modal, message, Tag, Tooltip, Popconfirm, DatePicker
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, 
  EyeOutlined, PlayCircleOutlined, PauseCircleOutlined, SyncOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { getJobList, deleteJob, runJob, pauseJob, resumeJob, batchDeleteJobs } from '@/api/modules/job';
import type { JobItem } from '@/api/modules/job';
import JobModal from './components/JobModal';

const { confirm } = Modal;
const { RangePicker } = DatePicker;

const JobList: React.FC = () => {
  const [list, setList] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobItem | null>(null);
  const [searchForm] = Form.useForm();
  
  const navigate = useNavigate();

  // 获取任务列表
  const fetchJobList = async () => {
    setLoading(true);
    try {
      const params = {
        ...searchForm.getFieldsValue(),
        page: pagination.current,
        limit: pagination.pageSize,
      };
      
      const res = await getJobList(params);
      if (res && res.data) {
        const items = (res.data as any)?.data ?? res.data;
        setList(Array.isArray(items) ? items : []);
        setTotal(res.total || 0);
      }
    } catch (error) {
      console.error('获取任务列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchJobList();
  }, [pagination.current, pagination.pageSize]);

  // 处理表格变更
  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchJobList();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    fetchJobList();
  };

  // 编辑任务
  const handleEdit = (record: JobItem) => {
    setCurrentJob(record);
    setModalVisible(true);
  };

  // 删除任务
  const handleDelete = async (id: string) => {
    try {
      await deleteJob(id);
      message.success('删除成功');
      fetchJobList();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要删除所选任务吗?',
      icon: <ExclamationCircleOutlined />,
      content: '此操作将永久删除所选任务，是否继续？',
      onOk: async () => {
        try {
          await batchDeleteJobs(selectedRowKeys.map(key => key.toString()));
          message.success('删除成功');
          fetchJobList();
          setSelectedRowKeys([]);
        } catch (error) {
          console.error('删除失败', error);
        }
      }
    });
  };

  // 执行任务
  const handleRunJob = async (id: string) => {
    try {
      await runJob(id);
      message.success('任务执行成功');
      fetchJobList();
    } catch (error) {
      console.error('执行失败', error);
    }
  };

  // 暂停任务
  const handlePauseJob = async (id: string) => {
    try {
      await pauseJob(id);
      message.success('任务已暂停');
      fetchJobList();
    } catch (error) {
      console.error('暂停失败', error);
    }
  };

  // 恢复任务
  const handleResumeJob = async (id: string) => {
    try {
      await resumeJob(id);
      message.success('任务已恢复');
      fetchJobList();
    } catch (error) {
      console.error('恢复失败', error);
    }
  };

  // 查看日志
  const handleViewLogs = (jobId: string) => {
    navigate(`/system/job/log?jobId=${jobId}`);
  };

  // 表单提交成功的回调
  const handleModalSuccess = () => {
    setModalVisible(false);
    fetchJobList();
  };

  const columns: ColumnsType<JobItem> = [
    {
      title: '任务ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '任务名称',
      dataIndex: 'jobName',
      ellipsis: true,
      width: 120,
    },
    {
      title: '任务组',
      dataIndex: 'jobGroup',
      width: 100,
    },
    {
      title: '调用目标',
      dataIndex: 'invokeTarget',
      ellipsis: true,
      width: 220,
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Cron表达式',
      dataIndex: 'cronExpression',
      width: 140,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 'running' ? 'green' : 'orange'}>
          {status === 'running' ? '运行中' : '已暂停'}
        </Tag>
      )
    },
    {
      title: '并发执行',
      dataIndex: 'concurrent',
      width: 80,
      render: (concurrent) => (
        <Tag color={concurrent ? 'blue' : 'default'}>
          {concurrent ? '允许' : '禁止'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 150,
      sorter: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap style={{ whiteSpace: 'nowrap' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            style={{ padding: '0 4px' }}
          >
            编辑
          </Button>
          
          {record.status === 'running' ? (
            <Button 
              type="link" 
              icon={<PauseCircleOutlined />} 
              size="small" 
              onClick={() => handlePauseJob(record.id)}
              style={{ padding: '0 4px' }}
            >
              暂停
            </Button>
          ) : (
            <Button 
              type="link" 
              icon={<PlayCircleOutlined />} 
              size="small" 
              onClick={() => handleResumeJob(record.id)}
              style={{ padding: '0 4px' }}
            >
              恢复
            </Button>
          )}
          
          <Button 
            type="link" 
            icon={<SyncOutlined />} 
            size="small"
            onClick={() => handleRunJob(record.id)}
            style={{ padding: '0 4px' }}
          >
            执行
          </Button>
          
          <Button 
            type="link" 
            icon={<HistoryOutlined />} 
            size="small"
            onClick={() => handleViewLogs(record.id)}
            style={{ padding: '0 4px' }}
          >
            日志
          </Button>
          
          <Popconfirm
            title="确定要删除此任务吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
              style={{ padding: '0 4px' }}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="job-list-container" style={{ width: '100%', overflowX: 'hidden' }}>
      <Card
        title="定时任务监控与配置"
        bordered={false}
      >
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="jobName">
            <Input placeholder="任务名称" allowClear />
          </Form.Item>
          <Form.Item name="jobGroup">
            <Input placeholder="任务组" allowClear />
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="任务状态"
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value="running">运行中</Select.Option>
              <Select.Option value="paused">已暂停</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={!selectedRowKeys.length}
            >
              批量删除
            </Button>
          </Space>
        </div>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
          style={{ overflowX: 'auto' }}
        />
      </Card>

      {/* 任务编辑对话框 */}
      <JobModal
        visible={modalVisible}
        job={currentJob}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default JobList;
